import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useLocation } from "react-router-dom";
import { X, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LS_SUBSCRIBED = "hk_popup_subscribed";
const SS_DISMISSED = "hk_popup_dismissed";

/** Homepage email-capture popup — config (enabled/heading/subtext/delay) comes from Admin › Subscribers. */
const EmailCapturePopup = () => {
  const { pathname } = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (pathname !== "/") return undefined;
    if (localStorage.getItem(LS_SUBSCRIBED) || sessionStorage.getItem(SS_DISMISSED)) return undefined;
    let alive = true;
    axios.get(`${API}/settings/popup`).then((r) => {
      if (!alive) return;
      const c = r.data || {};
      if (!c.enabled) return;
      setCfg(c);
      timerRef.current = setTimeout(() => alive && setOpen(true), Math.max(1, Number(c.delay_seconds) || 5) * 1000);
    }).catch(() => {});
    return () => { alive = false; clearTimeout(timerRef.current); };
  }, [pathname]);

  if (!open || !cfg) return null;

  const close = () => {
    sessionStorage.setItem(SS_DISMISSED, "1");
    setOpen(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    const val = email.trim();
    if (!val) return;
    const digits = phone.replace(/\D/g, "");
    if (digits && (digits.length < 10 || digits.length > 15)) {
      toast.error("Please enter a valid mobile number.");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/subscribers`, { email: val, phone: digits || undefined, source: "homepage_popup" });
      localStorage.setItem(LS_SUBSCRIBED, "1");
      setDone(true);
      setTimeout(() => setOpen(false), 3500);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not subscribe — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4" data-testid="email-popup" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/45 backdrop-blur-[2px]" onClick={close} data-testid="email-popup-overlay" />
      <div className="hk-card relative w-full max-w-md p-8 hk-fade-up" style={{ borderRadius: 18 }}>
        <button onClick={close} aria-label="Close popup" data-testid="email-popup-close"
          className="absolute top-3 right-3 p-2 rounded-full text-[var(--hk-ink-3)] hover:text-[var(--hk-ink)] hover:bg-black/5 transition-colors">
          <X size={18} />
        </button>

        {done ? (
          <div className="text-center py-4" data-testid="email-popup-success">
            <CheckCircle2 size={44} className="mx-auto mb-3 text-[var(--hk-green,#3a7d44)]" />
            <h3 className="text-xl font-semibold mb-2">Thank you for subscribing!</h3>
            <p className="text-sm text-[var(--hk-ink-3)]">You're on the list — watch your inbox for wellness wisdom and offers.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-2 mb-3">
              <BrandLogo variant="light" height={30} markSize={28} wordSize={14} showTagline={false} testId="popup-brand-logo" />
            </div>
            <h3 className="text-2xl font-semibold mb-2" data-testid="email-popup-heading">
              {cfg.heading || "Join the HerbKranti family"}
            </h3>
            <p className="text-sm text-[var(--hk-ink-3)] mb-5" data-testid="email-popup-subtext">
              {cfg.subtext || "Get wellness tips, new harvest updates and member-only offers."}
            </p>
            <form onSubmit={submit} className="flex flex-col gap-3">
              <input
                type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address" className="hk-input h-11"
                data-testid="email-popup-input" autoFocus
              />
              <input
                type="tel" inputMode="numeric" value={phone} onChange={(e) => setPhone(e.target.value)}
                placeholder="Mobile number (optional)" className="hk-input h-11" maxLength={16}
                data-testid="email-popup-phone-input"
              />
              <button type="submit" disabled={busy} className="hk-btn-primary h-11 px-6 inline-flex items-center justify-center gap-2"
                data-testid="email-popup-submit">
                {busy && <Loader2 size={15} className="animate-spin" />} Subscribe
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default EmailCapturePopup;
